import { observable, action, computed } from "mobx";
import { Api } from "services/Api";
import axios from "axios";
import _ from "lodash";
import moment from "moment";
import stores from "stores";
import { timelineLayoutEnd, realMinutes } from "helpers/timelineLayout";
import { applyCalendarEdits } from "helpers/localCalendarEdits";
import { priorityQueryValues } from "helpers/priorityTag";

class DashboardStore {
  @observable jobs = {
    isLoading: false,
    isError: false,
    objects: [],
  };

  @observable assignments = {
    isLoading: false,
    isError: false,
    objects: [],
  };

  @observable users = {
    isLoading: false,
    isError: false,
    objects: [],
  };

  @computed
  get isLoading() {
    return (
      this.assignments.isLoading || this.users.isLoading || this.jobs.isLoading
    );
  }

  @action
  loadWeekViewJobs(start, end) {
    this.assignments.isLoading = true;
    const selectedUsers = stores.viewStore.dashboardSearch.technician_id;

    const orObject = [];
    if (selectedUsers && selectedUsers.length > 0) {
      orObject.push({
        user_id: selectedUsers.filter((id) => id !== "0" && id !== "selectAll"),
      });
      if (selectedUsers.includes("0")) {
        orObject.push({
          "assignments.user_id": null,
        });
      }
    }

    let promise = Api.post("/jobs/search", {
      search: {
        where: {
          service_type_id: stores.viewStore.dashboardSearch.service_type_id,
          priority: priorityQueryValues(stores.viewStore.dashboardSearch.priority),
          _or: [
            {
              start_date: {
                gte: moment(start).subtract(7, "days"),
                lte: moment(end).add(7, "days"),
              },
              _or: orObject,
            },
            {
              end_date: {
                gte: moment(start).subtract(7, "days"),
                lte: moment(end).add(7, "days"),
              },
              _or: orObject,
            },
          ],
        },
        per_page: 425,
      },
    });

    promise
      .then((response) => {
        this.assignments.isLoading = false;
        this.assignments.isError = false;
        this.assignments.objects = response.data.data;
      })
      .catch((error) => {
        this.assignments.isLoading = false;
        this.assignments.isError = true;
      });
    return promise;
  }

  loadCalendarAssignments(start, end, timezone, callback) {
    const view = stores.viewStore.dashboardSearch.view;
    let rangeStart, rangeEnd;

    // Determine date range based on view type
    if (view === "timelineDay") {
      // For timelineDay: use startOfDay and endOfDay of the selected date
      rangeStart = moment(start).startOf("day").toISOString();
      rangeEnd = moment(start).endOf("day").toISOString();
    } else if (view === "basicWeek") {
      // For basicWeek: use startOfWeek and endOfWeek
      rangeStart = moment(start).startOf("week").toISOString();
      rangeEnd = moment(end).endOf("week").toISOString();
    } else {
      // For other views, use the provided start and end
      rangeStart = moment(start).startOf("day").toISOString();
      rangeEnd = moment(end).endOf("day").toISOString();
    }

    // Fetch vacations from backend
    const vacationsPromise = this.loadVacations(rangeStart, rangeEnd);

    // TODO: adding +1 day in start date and end date
    // Hack to fix the timezone issue, need to fix this
    if (view === "basicWeek") {
      const jobsPromise = this.loadWeekViewJobs(
        start.set("date", start.get("date") - 1),
        end.set("date", end.get("date") + 1)
      );

      Promise.all([jobsPromise, vacationsPromise])
        .then(([jobsResponse, vacationsResponse]) => {
          let events = jobsResponse.data.data.map((job) => {
            return {
              id: job.id,
              title: job.attributes.title,
              eventNumber: job.attributes.event_number,
              subtitle: `${job.attributes.short_start} - ${job.attributes.short_end}`,
              start: job.attributes.start_date,
              end: job.attributes.end_date,
              short_start: job.attributes.short_start,
              short_end: job.attributes.short_end,
              endForDisplay: job.attributes.end_date,
              resourceIds: job.attributes.technician_ids
                ? job.attributes.technician_ids
                : ["0"],
              clientName: job.attributes.client.name,
              fullAddress:
                job.attributes.location && job.attributes.location.full_address,
              latitude: job.attributes.location && job.attributes.location.latitude,
              longitude: job.attributes.location && job.attributes.location.longitude,
              technicianName: job.attributes.technician_names,
              displayEventTime: true,
              statusColor: "",
              jobId: job.id,
              serviceType: job.attributes.service_type,
              status: job.attributes.status,
              job_status: job.attributes.status,
              isRecurringJob: job.attributes.service_agreement.id
                ? true
                : false,
              assignments: job.attributes.assignments,
              priority: (job.attributes.priority || "").toLowerCase(),
            };
          });

          // Map vacations from backend response
          const vacations = (vacationsResponse.data.data || []).map(
            (vacation) => {
              return {
                id: vacation.id,
                user:
                  vacation.attributes.full_name ||
                  vacation.attributes.user_name ||
                  "Unknown",
                start: vacation.attributes.start_date,
                end: vacation.attributes.end_date,
                title: vacation.attributes.notes || "",
                editable: false,
                isVacation: true,
                resourceIds: vacation.attributes.user_id
                  ? [vacation.attributes.user_id.toString()]
                  : [],
              };
            }
          );

          events = events.concat(vacations);
          // Keep positions the user just set until the server catches up.
          callback(applyCalendarEdits(events));
        })
        .catch((error) => {
          console.error("Error loading calendar data:", error);
          callback([]);
        });
    } else {
      const assignmentsPromise = this.loadAssignments(
        start.set("date", start.get("date") - 1),
        end.set("date", end.get("date") + 1)
      );

      Promise.all([assignmentsPromise, vacationsPromise])
        .then(([assignmentsResponse, vacationsResponse]) => {
          let events = assignmentsResponse.data.data.map((assignment) => {
            return {
              id: assignment.id,
              title: assignment.attributes.title,
              eventNumber: assignment.attributes.event_number,
              subtitle: `${assignment.attributes.short_start} - ${assignment.attributes.short_end}`,
              start: assignment.attributes.start_date,
              // Layout end (at least TIMELINE_MIN_LAYOUT_MINUTES) so cards
              // never overlap; the real end is endForDisplay.
              end: timelineLayoutEnd(
                assignment.attributes.start_date,
                assignment.attributes.end_date
              ),
              realMinutes: realMinutes(
                assignment.attributes.start_date,
                assignment.attributes.end_date
              ),
              short_start: assignment.attributes.short_start,
              short_end: assignment.attributes.short_end,
              endForDisplay: assignment.attributes.end_date,
              resourceId: assignment.attributes.user_id
                ? assignment.attributes.user_id
                : 0,
              clientName: assignment.attributes.client.name,
              fullAddress:
                assignment.attributes.location &&
                assignment.attributes.location.full_address,
              latitude:
                assignment.attributes.location &&
                assignment.attributes.location.latitude,
              longitude:
                assignment.attributes.location &&
                assignment.attributes.location.longitude,
              technicianName: assignment.attributes.technician_name,
              displayEventTime: true,
              statusColor: assignment.attributes.assignment_color,
              jobId: assignment.attributes.job_id,
              serviceType: assignment.attributes.service_type,
              status: assignment.attributes.status,
              job_status:
                (assignment.attributes.job &&
                  assignment.attributes.job.status) ||
                assignment.attributes.job_status,
              isRecurringJob: assignment.attributes.service_agreement.id
                ? true
                : false,
              priority: (assignment.attributes.priority || (assignment.attributes.job && assignment.attributes.job.priority) || "").toLowerCase(),
            };
          });

          // Map vacations from backend response
          const vacations = (vacationsResponse.data.data || []).map(
            (vacation) => {
              return {
                id: vacation.id,
                user:
                  vacation.attributes.full_name ||
                  vacation.attributes.user_name ||
                  "Unknown",
                start: vacation.attributes.start_date,
                end: vacation.attributes.end_date,
                title: vacation.attributes.notes || "",
                editable: false,
                isVacation: true,
                resourceId: vacation.attributes.user_id
                  ? vacation.attributes.user_id.toString()
                  : "0",
              };
            }
          );

          events = events.concat(vacations);
          // Keep positions the user just set until the server catches up.
          callback(applyCalendarEdits(events));
        })
        .catch((error) => {
          console.error("Error loading calendar data:", error);
          callback([]);
        });
    }
  }

  loadCalendarUsers(callback) {
    let promise = Api.post("/users/search", {
      role: ["technician", "admin"],
      include_unassigned: true,
    });

    promise
      .then((users) => {
        this.users.objects = users.data.data;
        let resources = users.data.data.map((user) => {
          if (user.id) {
            return {
              id: user.id,
              title: user.attributes.full_name,
              avatar_url: user.attributes.avatar_url,
            };
          } else {
            return {
              id: "0",
              title: user.attributes.full_name,
              avatar_url: user.attributes.avatar_url,
            };
          }
        });
        const selectedUsers = stores.viewStore.dashboardSearch.technician_id;
        if (selectedUsers && selectedUsers.length > 0) {
          resources = resources.filter((r) => selectedUsers.includes(r.id));
        }
        callback(resources);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
        } else {
        }
      });

    return promise;
  }

  // Fix: Vacation load issue on dispatch board first load.
  fetchDashboardUsers() {
    let promise = Api.post("/users/search", {
      role: ["technician", "admin"],
      include_unassigned: true,
    });

    promise
      .then((users) => {
        this.users.objects = users.data.data;
      })
      .catch((error) => { });

    return promise;
  }

  @action
  loadAssignments(start, end) {
    this.assignments.isLoading = true;
    const selectedUsers = stores.viewStore.dashboardSearch.technician_id;

    const orObject = [];
    if (selectedUsers && selectedUsers.length > 0) {
      orObject.push({
        user_id:
          selectedUsers &&
          selectedUsers.filter((id) => id !== "0" && id !== "selectAll"),
      });
      if (selectedUsers.includes("0")) {
        orObject.push({
          status: "unassigned",
        });
      }
    }

    let promise = Api.post("/assignments/search", {
      search: {
        where: {
          service_type_id: stores.viewStore.dashboardSearch.service_type_id,
          priority: priorityQueryValues(stores.viewStore.dashboardSearch.priority),
          _or: [
            {
              start_date: {
                gte: moment(start).subtract(7, "days"),
                lte: moment(end).add(7, "days"),
              },
              _or: orObject,
            },
            {
              end_date: {
                gte: moment(start).subtract(7, "days"),
                lte: moment(end).add(7, "days"),
              },
              _or: orObject,
            },
          ],
        },
      },
    });

    promise
      .then((response) => {
        this.assignments.isLoading = false;
        this.assignments.isError = false;
        this.assignments.objects = response.data.data;
      })
      .catch((error) => {
        this.assignments.isLoading = false;
        this.assignments.isError = true;
        console.error(error);
      });
    return promise;
  }

  @action
  // Users page list. `where.role` (admin/technician) and `where.skills` (users
  // with any of them) are filtered by the API;
  // `where.status` is sent too but the API doesn't support it yet, so
  // components/Tables/Users also filters by status in the browser.
  loadUsers({ section, where = {} }) {
    this.users.isLoading = true;
    const roles = where.role || [];
    const payload = section === "invitations"
      ? { role: "invitations" }
      : { role: roles.length ? roles : "all", skills: where.skills || [], status: where.status || [] };
    let promise = Api.post("/users/search", payload);

    promise
      .then((response) => {
        this.users.isLoading = false;
        this.users.isError = false;
        this.users.objects = response.data.data;
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          this.users.isLoading = true;
        } else {
          this.users.isLoading = false;
          this.users.isError = true;
        }
      });

    return promise;
  }

  @action
  loadVacations(rangeStart, rangeEnd) {
    const selectedUsers = stores.viewStore.dashboardSearch.technician_id;

    let whereClause = {
      _or: [
        {
          start_date: {
            gte: rangeStart,
            lte: rangeEnd,
          },
        },
        {
          end_date: {
            gte: rangeStart,
            lte: rangeEnd,
          },
        },
        {
          start_date: {
            lte: rangeStart,
          },
          end_date: {
            gte: rangeStart,
          },
        },
        {
          start_date: {
            lte: rangeEnd,
          },
          end_date: {
            gte: rangeEnd,
          },
        },
      ],
    };

    // Filter by selected users if any
    if (selectedUsers && selectedUsers.length > 0) {
      const userIds = selectedUsers.filter(
        (id) => id !== "0" && id !== "selectAll"
      );
      if (userIds.length > 0) {
        whereClause.user_id = userIds;
      }
    }

    let promise = Api.post("/vacations/search", {
      search: {
        where: whereClause,
        per_page: 1000,
      },
    });

    return promise;
  }
}

export default DashboardStore;
